/**
 * PdfPopupViewer — draws a PDF inside an EVOX pop-up (or the announcement archive page) using
 * pdf.js via react-pdf, instead of handing the file to the browser's PDF plugin in an <iframe>.
 *
 * Why: the browser plugin owns everything inside its frame. It navigates the EVOX page away when
 * a link inside the PDF is clicked, it clips tall pages to the frame height, and it does not
 * render at all inside a frame on Android. Drawing the PDF ourselves means the page owns the
 * links (they open in a new tab), the fit (whole page visible), and mobile support.
 *
 * Usage:
 *   <PdfPopupViewer src={media.url} title={item.title} />
 *
 * The announcements engine resolves the per-country PDF variant server-side and hands the url in
 * as `src`; nothing here needs to change when a publisher uploads a new file.
 *
 * Versions are coupled: react-pdf 5.7.2 bundles pdf.js 2.12.313, and the worker file served
 * from public/plugins/pdfjs/pdf.worker.min.js MUST be the same pdf.js version. When react-pdf
 * is upgraded, copy the new worker from
 *   node_modules/react-pdf/node_modules/pdfjs-dist/legacy/build/pdf.worker.min.js
 * (nested there because another package pins an older pdfjs-dist at the top level).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './PdfPopupViewer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/plugins/pdfjs/pdf.worker.min.js`;

// Height the poster may take before it is shrunk to fit. Leaves room for the modal header
// and the browser chrome so a portrait poster is fully visible without scrolling the modal.
const MODAL_CHROME_PX = 180;
const MIN_HEIGHT_PX = 400;

function defaultMaxHeight() {
  if (typeof window === 'undefined') return 730;
  return Math.max(MIN_HEIGHT_PX, window.innerHeight - MODAL_CHROME_PX);
}

export default function PdfPopupViewer({ src, title, maxHeight }) {
  const boxRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pageAspect, setPageAspect] = useState(null); // width / height of page 1

  useEffect(() => {
    const measure = () => setBoxWidth(boxRef.current ? boxRef.current.clientWidth : 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const onDocumentLoad = useCallback((pdf) => setNumPages(pdf.numPages), []);
  const onFirstPageLoad = useCallback((page) => {
    if (page && page.originalWidth && page.originalHeight) {
      setPageAspect(page.originalWidth / page.originalHeight);
    }
  }, []);
  const onDocumentError = useCallback((err) => {
    // Not fatal for the user: react-pdf swaps in the `error` content below.
    // eslint-disable-next-line no-console
    console.warn('PdfPopupViewer: could not render', src, err && err.message);
  }, [src]);

  const heightLimit = maxHeight || defaultMaxHeight();
  let pageWidth = boxWidth || undefined;
  if (boxWidth && pageAspect) {
    pageWidth = Math.min(boxWidth, Math.floor(heightLimit * pageAspect));
  }

  const fallback = (
    <div className="pdf-popup-viewer__fallback">
      <p>The document could not be displayed here.</p>
      <a href={src} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
        Open {title}
      </a>
    </div>
  );

  return (
    <div className="pdf-popup-viewer" ref={boxRef} role="region" aria-label={title}>
      <Document
        file={src}
        externalLinkTarget="_blank"
        externalLinkRel="noopener noreferrer"
        onLoadSuccess={onDocumentLoad}
        onLoadError={onDocumentError}
        onSourceError={onDocumentError}
        loading={<div className="pdf-popup-viewer__loading">Loading {title}…</div>}
        error={fallback}
        noData={fallback}
      >
        {Array.from({ length: numPages }, (_, i) => (
          <Page
            key={i + 1}
            pageNumber={i + 1}
            width={pageWidth}
            renderTextLayer={false}
            renderAnnotationLayer={true}
            onLoadSuccess={i === 0 ? onFirstPageLoad : undefined}
          />
        ))}
      </Document>
    </div>
  );
}
